/**
 * Simpson's Method (Method of Disks) Calculator for Echocardiography
 * Based on ASE 2015 Guidelines
 *
 * Use with Cornerstone.js PlanarFreehandROITool
 */

import type {
  Point2D,
  SimpsonDisk,
  SimpsonResult,
  SimpsonOptions,
  AnnotationPoint,
  CornerstoneAnnotation,
  CornerstoneViewport,
} from './types';

/**
 * Calculates volume using Simpson's Method (single-plane)
 *
 * @param contourPoints - Array of contour points (from FreehandROI)
 * @param options - Configuration options
 * @returns SimpsonResult with volume and rendering data
 */
export function calculateSimpsonVolume(
  contourPoints: Point2D[],
  options: SimpsonOptions = {}
): SimpsonResult {
  const { numberOfDisks = 20, pixelSpacing = 1 } = options;

  if (contourPoints.length < 10) {
    throw new Error(
      'At least 10 points are required to calculate Simpson volume'
    );
  }

  // 1. Find the apex and base
  const { apex, baseLeft, baseRight, baseMidpoint } =
    findApexAndBase(contourPoints);

  // 2. Calculate the long axis length
  const longAxisLength = calculateDistance(apex, baseMidpoint);

  // 3. Calculate the height of each disk
  const diskHeight = longAxisLength / numberOfDisks;

  // 4. Generate the disks
  const disks: SimpsonDisk[] = [];
  let totalVolume = 0;

  for (let i = 0; i < numberOfDisks; i += 1) {
    // Disk Y position (from apex toward base)
    const diskPositionRatio = (i + 0.5) / numberOfDisks; // Disk center

    // Find the intersection points of the contour with the disk line
    const { leftPoint, rightPoint, diameter } = findDiskIntersections(
      contourPoints,
      apex,
      baseMidpoint,
      diskPositionRatio
    );

    // Calculate disk area and volume
    const diameterMm = diameter * pixelSpacing;
    const diskArea = (Math.PI / 4) * diameterMm * diameterMm;
    const diskVolume = diskArea * (diskHeight * pixelSpacing);

    disks.push({
      index: i,
      centerY: diskPositionRatio,
      diameter: diameterMm,
      area: diskArea,
      leftPoint,
      rightPoint,
    });

    totalVolume += diskVolume;
  }

  // Convert volume from mm³ to mL (1 mL = 1000 mm³)
  const volumeML = totalVolume / 1000;

  return {
    volume: Math.round(volumeML * 10) / 10, // Round to 1 decimal
    volumeUnit: 'mL',
    longAxisLength: Math.round(longAxisLength * pixelSpacing * 10) / 10,
    longAxisUnit: 'mm',
    numberOfDisks,
    disks,
    apex,
    baseMidpoint,
    baseLeft,
    baseRight,
  };
}

/**
 * Finds the apex and base points of the contour
 * Assumes the contour is oriented with apex at top (typical in apical echo views)
 */
function findApexAndBase(points: Point2D[]): {
  apex: Point2D;
  baseLeft: Point2D;
  baseRight: Point2D;
  baseMidpoint: Point2D;
} {
  // Find the contour bounds
  let minY = Infinity;
  let maxY = -Infinity;
  let minX = Infinity;
  let maxX = -Infinity;

  points.forEach((p) => {
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
  });

  // The apex is generally the point furthest from the base
  // In an apical 4-chamber view, the apex is at top (lower Y on screen)
  // The base is the widest line (higher Y)

  // Find the apex point (highest point, near center X)
  const centerX = (minX + maxX) / 2;
  let apex: Point2D = points[0];
  let minApexY = Infinity;

  points.forEach((p) => {
    // Search points in the upper third
    if (p.y < minY + (maxY - minY) * 0.3) {
      // Prefer points near the horizontal center
      const distFromCenter = Math.abs(p.x - centerX);
      if (
        p.y < minApexY ||
        (p.y === minApexY && distFromCenter < Math.abs(apex.x - centerX))
      ) {
        apex = p;
        minApexY = p.y;
      }
    }
  });

  // Find the base points (the two lowest points on opposite ends)
  // The base is where the contour closes (mitral annulus)

  // Search points in the lower third
  const basePoints = points.filter((p) => p.y > maxY - (maxY - minY) * 0.2);

  // Separate into left and right sides
  const leftBasePoints = basePoints.filter((p) => p.x < centerX);
  const rightBasePoints = basePoints.filter((p) => p.x >= centerX);

  // Find the lowest points on each side
  let baseLeft: Point2D = leftBasePoints[0] || points[0];
  let baseRight: Point2D = rightBasePoints[0] || points[0];

  leftBasePoints.forEach((p) => {
    if (p.y > baseLeft.y) baseLeft = p;
  });

  rightBasePoints.forEach((p) => {
    if (p.y > baseRight.y) baseRight = p;
  });

  // Base midpoint
  const baseMidpoint: Point2D = {
    x: (baseLeft.x + baseRight.x) / 2,
    y: (baseLeft.y + baseRight.y) / 2,
  };

  return { apex, baseLeft, baseRight, baseMidpoint };
}

/**
 * Calculates the distance between two points
 */
function calculateDistance(p1: Point2D, p2: Point2D): number {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

/**
 * Interpolates a point along a line
 */
function interpolatePoint(
  start: Point2D,
  end: Point2D,
  ratio: number
): Point2D {
  return {
    x: start.x + (end.x - start.x) * ratio,
    y: start.y + (end.y - start.y) * ratio,
  };
}

/**
 * Finds the intersections of the contour with a line perpendicular to the long axis
 */
function findDiskIntersections(
  contourPoints: Point2D[],
  apex: Point2D,
  baseMidpoint: Point2D,
  positionRatio: number
): { leftPoint: Point2D; rightPoint: Point2D; diameter: number } {
  // Point on the long axis where the disk passes
  const diskCenter = interpolatePoint(apex, baseMidpoint, positionRatio);

  // Long axis vector
  const axisVector = {
    x: baseMidpoint.x - apex.x,
    y: baseMidpoint.y - apex.y,
  };

  // Perpendicular vector (rotate 90 degrees)
  const perpVector = {
    x: -axisVector.y,
    y: axisVector.x,
  };

  // Normalize
  const perpLength = Math.sqrt(
    perpVector.x * perpVector.x + perpVector.y * perpVector.y
  );
  const perpUnit = {
    x: perpVector.x / perpLength,
    y: perpVector.y / perpLength,
  };

  // Find intersections with the contour
  const intersections: Point2D[] = [];

  for (let i = 0; i < contourPoints.length; i += 1) {
    const p1 = contourPoints[i];
    const p2 = contourPoints[(i + 1) % contourPoints.length];

    const intersection = lineSegmentIntersection(
      diskCenter,
      {
        x: diskCenter.x + perpUnit.x * 1000,
        y: diskCenter.y + perpUnit.y * 1000,
      },
      p1,
      p2
    );

    if (intersection) {
      intersections.push(intersection);
    }
  }

  // If not enough intersections found, use approximation
  if (intersections.length < 2) {
    // Find the nearest points to the disk line
    const nearPoints = findNearestPointsToLine(
      contourPoints,
      diskCenter,
      perpUnit
    );
    return nearPoints;
  }

  // Sort by position on the perpendicular axis
  intersections.sort((a, b) => {
    const projA =
      (a.x - diskCenter.x) * perpUnit.x + (a.y - diskCenter.y) * perpUnit.y;
    const projB =
      (b.x - diskCenter.x) * perpUnit.x + (b.y - diskCenter.y) * perpUnit.y;
    return projA - projB;
  });

  // Take the two most extreme points
  const leftPoint = intersections[0];
  const rightPoint = intersections[intersections.length - 1];
  const diameter = calculateDistance(leftPoint, rightPoint);

  return { leftPoint, rightPoint, diameter };
}

/**
 * Finds the intersection between a line and a segment
 */
function lineSegmentIntersection(
  linePoint1: Point2D,
  linePoint2: Point2D,
  segStart: Point2D,
  segEnd: Point2D
): Point2D | null {
  const x1 = segStart.x;
  const y1 = segStart.y;
  const x2 = segEnd.x;
  const y2 = segEnd.y;
  const x3 = linePoint1.x;
  const y3 = linePoint1.y;
  const x4 = linePoint2.x;
  const y4 = linePoint2.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return null;

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

  if (t < 0 || t > 1) return null;

  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1),
  };
}

/**
 * Finds the nearest points to a line (fallback method)
 */
function findNearestPointsToLine(
  points: Point2D[],
  lineCenter: Point2D,
  perpUnit: { x: number; y: number }
): { leftPoint: Point2D; rightPoint: Point2D; diameter: number } {
  let leftPoint: Point2D = points[0];
  let rightPoint: Point2D = points[0];
  let minLeftProj = Infinity;
  let maxRightProj = -Infinity;

  // Long axis vector
  const axisVector = { x: -perpUnit.y, y: perpUnit.x };

  points.forEach((p) => {
    // Projection on the perpendicular axis
    const perpProj =
      (p.x - lineCenter.x) * perpUnit.x + (p.y - lineCenter.y) * perpUnit.y;

    // Projection on the long axis (distance to disk)
    const axisProj = Math.abs(
      (p.x - lineCenter.x) * axisVector.x + (p.y - lineCenter.y) * axisVector.y
    );

    // Only consider points near the disk level
    if (axisProj < 20) {
      // 20 pixels tolerance
      if (perpProj < minLeftProj) {
        minLeftProj = perpProj;
        leftPoint = p;
      }
      if (perpProj > maxRightProj) {
        maxRightProj = perpProj;
        rightPoint = p;
      }
    }
  });

  return {
    leftPoint,
    rightPoint,
    diameter: calculateDistance(leftPoint, rightPoint),
  };
}

/**
 * Calculates Ejection Fraction using two Simpson measurements
 */
export function calculateEjectionFraction(
  edvResult: SimpsonResult,
  esvResult: SimpsonResult
): {
  ef: number;
  strokeVolume: number;
  edv: number;
  esv: number;
} {
  const edv = edvResult.volume;
  const esv = esvResult.volume;
  const strokeVolume = edv - esv;
  const ef = ((edv - esv) / edv) * 100;

  return {
    ef: Math.round(ef * 10) / 10,
    strokeVolume: Math.round(strokeVolume * 10) / 10,
    edv: Math.round(edv * 10) / 10,
    esv: Math.round(esv * 10) / 10,
  };
}

/**
 * Extracts contour points from a PlanarFreehandROI annotation
 * Handles multiple Cornerstone versions and annotation structures
 */
export function getContourPointsFromAnnotation(
  annotation: CornerstoneAnnotation
): Point2D[] {
  if (
    annotation.data?.contour?.polyline &&
    Array.isArray(annotation.data.contour.polyline)
  ) {
    const { polyline } = annotation.data.contour;

    return polyline.map((p: AnnotationPoint) => {
      // Handle both [x, y] and [x, y, z] formats
      if (Array.isArray(p)) {
        return { x: p[0], y: p[1] };
      }
      // Handle object format {x, y, z}
      if (typeof p === 'object' && p !== null) {
        return { x: p.x, y: p.y };
      }
      throw new Error(`Unknown point format: ${JSON.stringify(p)}`);
    });
  }

  // Alternative: handles.points (some Cornerstone versions)
  if (
    annotation.data?.handles?.points &&
    Array.isArray(annotation.data.handles.points)
  ) {
    const { points } = annotation.data.handles;

    return points.map((p: AnnotationPoint) => {
      if (Array.isArray(p)) {
        return { x: p[0], y: p[1] };
      }
      if (typeof p === 'object' && p !== null) {
        return { x: p.x, y: p.y };
      }
      throw new Error(`Unknown point format: ${JSON.stringify(p)}`);
    });
  }

  // Cornerstone Tools legacy (v3)
  if (annotation.handles?.points && Array.isArray(annotation.handles.points)) {
    const { points } = annotation.handles;

    return points.map((p: AnnotationPoint) => {
      if (Array.isArray(p)) {
        return { x: p[0], y: p[1] };
      }
      return { x: p.x, y: p.y };
    });
  }

  throw new Error('Could not find contour points in the annotation');
}

/**
 * Gets pixel spacing from viewport/image
 */
export function getPixelSpacingFromViewport(
  viewport: CornerstoneViewport
): number {
  // Try to get from image metadata
  try {
    const imageData = viewport.getImageData?.();
    if (imageData?.spacing) {
      // Average of X and Y spacing
      return (imageData.spacing[0] + imageData.spacing[1]) / 2;
    }

    // Alternative: get from current image
    const image = viewport.csImage || viewport.getImage?.();
    if (image?.columnPixelSpacing && image?.rowPixelSpacing) {
      return (image.columnPixelSpacing + image.rowPixelSpacing) / 2;
    }
  } catch {
    throw new Error('Could not get pixel spacing, using default 1mm');
  }

  return 1;
}
